
import React from "react";
import { useAuth } from "@/contexts/auth-context";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, BarChart3, Newspaper, TrendingUp, ScrollText } from "lucide-react";

export default function Insights() {
  const { user } = useAuth();
  
  const articles = [
    {
      id: 1,
      title: "The Future of AI Agents in Business",
      excerpt: "Explore how AI agents are transforming business operations across industries.",
      author: "Maria Silva",
      category: "Business",
      date: "2025-04-15",
      readTime: "5 min",
      image: "/placeholder.svg"
    },
    {
      id: 2,
      title: "Building Ethical AI Systems",
      excerpt: "Why ethics should be at the forefront of AI development and implementation.",
      author: "Carlos Mendes",
      category: "Ethics",
      date: "2025-04-12",
      readTime: "8 min",
      image: "/placeholder.svg"
    },
    {
      id: 3,
      title: "Open-Source AI Models: The Next Frontier",
      excerpt: "How open-source is democratizing access to advanced AI capabilities.",
      author: "Julia Pereira",
      category: "Technology",
      date: "2025-04-10",
      readTime: "6 min",
      image: "/placeholder.svg"
    },
    {
      id: 4,
      title: "AI Agents for Personal Productivity",
      excerpt: "Practical ways to use AI agents to boost your daily productivity.",
      author: "Rafael Costa",
      category: "Productivity",
      date: "2025-04-08",
      readTime: "4 min",
      image: "/placeholder.svg"
    },
    {
      id: 5,
      title: "The Evolution of Large Language Models",
      excerpt: "From GPT-3 to the latest multimodal AI systems: what's changed and what's next.",
      author: "Ana Ferreira",
      category: "Technology",
      date: "2025-04-05",
      readTime: "10 min",
      image: "/placeholder.svg"
    },
    {
      id: 6,
      title: "AI in Healthcare: Transforming Patient Care",
      excerpt: "How AI agents are helping healthcare providers improve diagnostics and treatment.",
      author: "Pedro Santos",
      category: "Healthcare",
      date: "2025-04-03",
      readTime: "7 min",
      image: "/placeholder.svg"
    }
  ];
  
  const trends = [
    {
      id: 1,
      title: "Multimodal AI",
      description: "AI systems that can process multiple types of inputs (text, images, audio).",
      change: "+34%",
      positive: true
    },
    {
      id: 2,
      title: "AI Regulation",
      description: "Government policies and frameworks for responsible AI use.",
      change: "+28%",
      positive: true
    },
    {
      id: 3,
      title: "Autonomous Agents",
      description: "Self-directed AI systems that can perform complex tasks independently.",
      change: "+42%",
      positive: true
    },
    {
      id: 4,
      title: "AI Hallucinations",
      description: "Concerns about AI generating false or misleading information.",
      change: "-8%",
      positive: false
    },
    {
      id: 5,
      title: "Decentralized AI",
      description: "Moving away from centralized AI services toward distributed systems.",
      change: "+15%",
      positive: true
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-start md:flex-row md:items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Insights</h1>
            <p className="text-muted-foreground">Explore the latest trends and news in AI and Agents</p>
          </div>
        </div>
        
        <Tabs defaultValue="articles" className="space-y-6">
          <div className="flex justify-between items-center">
            <TabsList>
              <TabsTrigger value="articles" className="flex items-center gap-1">
                <ScrollText className="h-4 w-4" />
                <span>Articles</span>
              </TabsTrigger>
              <TabsTrigger value="trends" className="flex items-center gap-1">
                <TrendingUp className="h-4 w-4" />
                <span>Trends</span>
              </TabsTrigger>
              <TabsTrigger value="news" className="flex items-center gap-1">
                <Newspaper className="h-4 w-4" />
                <span>News</span>
              </TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="articles" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article) => (
                <Card key={article.id} className="overflow-hidden hover:shadow-md transition-shadow">
                  <div className="aspect-video bg-muted relative">
                    <img 
                      src={article.image} 
                      alt={article.title}
                      className="object-cover w-full h-full"
                    />
                    <Badge className="absolute top-3 right-3">{article.category}</Badge>
                  </div>
                  <CardHeader className="p-4 pb-2">
                    <CardTitle className="text-lg line-clamp-2">{article.title}</CardTitle>
                    <CardDescription className="line-clamp-2">{article.excerpt}</CardDescription>
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-2">
                        <Avatar className="h-6 w-6">
                          <AvatarFallback>{article.author[0]}</AvatarFallback>
                        </Avatar>
                        <span className="text-sm text-muted-foreground">{article.author}</span>
                      </div>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Calendar className="mr-1 h-3 w-3" />
                        <span>{new Date(article.date).toLocaleDateString()}</span>
                        <span className="mx-1">•</span>
                        <Clock className="mr-1 h-3 w-3" />
                        <span>{article.readTime}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="trends" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Current AI Trends
                </CardTitle>
                <CardDescription>
                  The most discussed topics in AI over the past month
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {trends.map((trend) => (
                    <div key={trend.id} className="flex items-center justify-between pb-4 border-b last:border-0">
                      <div>
                        <h3 className="font-medium">{trend.title}</h3>
                        <p className="text-sm text-muted-foreground">{trend.description}</p>
                      </div>
                      <Badge variant={trend.positive ? "default" : "destructive"} className="ml-4">
                        {trend.change}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="news" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Latest News</CardTitle>
                <CardDescription>Breaking news in the AI industry</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">Coming soon! We're gathering the latest news for you.</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
